// V3 — Time Concentration matrix. Sub-discipline cycler picks the active
// sub-discipline; rows = OMCL sub-phases, columns = L1..L8 percent inputs.

import { useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  OMCL_PHASES,
  OMCL_SUB_PHASES,
  SUB_DISCIPLINES,
} from '@rc/engine';
import type {
  LevelId,
  Model,
  TimeConcentrationRow,
} from '@rc/engine/types';

interface Props {
  model: Model;
  onChange: (m: Model) => void;
}

export default function TimeConcentrationEditor({ model, onChange }: Props) {
  const [subDisciplineKey, setSubDisciplineKey] = useState<string>(SUB_DISCIPLINES[0]?.key || '');
  const levels = model.levels || [];
  const rows = model.timeConcentration || [];

  const subPhases = OMCL_SUB_PHASES;

  const find = (subPhaseId: string, levelId: LevelId): TimeConcentrationRow | undefined =>
    rows.find(
      (r) =>
        r.subDisciplineKey === subDisciplineKey &&
        r.subPhaseId === subPhaseId &&
        r.levelId === levelId,
    );

  const set = (subPhaseId: string, levelId: LevelId, share: number) => {
    const clamped = Math.max(0, Math.min(1, share));
    const next = [...rows];
    const idx = next.findIndex(
      (r) =>
        r.subDisciplineKey === subDisciplineKey &&
        r.subPhaseId === subPhaseId &&
        r.levelId === levelId,
    );
    if (idx >= 0) next[idx] = { ...next[idx], share: clamped };
    else next.push({ subDisciplineKey, subPhaseId, levelId, share: clamped });
    onChange({ ...model, timeConcentration: next });
  };

  // Column sums (per level) — should be ~1. A level with no rows at all is a
  // level this sub-discipline does not staff (Business Leadership starts at
  // L5), which is different from one whose shares fail to add up.
  const { colSums, staffedLevels } = useMemo(() => {
    const sums: Partial<Record<LevelId, number>> = {};
    const staffed = new Set<LevelId>();
    for (const r of rows) {
      if (r.subDisciplineKey !== subDisciplineKey) continue;
      sums[r.levelId] = (sums[r.levelId] || 0) + (Number(r.share) || 0);
      staffed.add(r.levelId);
    }
    return { colSums: sums, staffedLevels: staffed };
  }, [rows, subDisciplineKey]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <label className="label !mb-0">Sub-discipline</label>
        <select
          className="input max-w-md"
          value={subDisciplineKey}
          onChange={(e) => setSubDisciplineKey(e.target.value)}
        >
          {SUB_DISCIPLINES.map((sd) => (
            <option key={sd.key} value={sd.key}>
              {sd.discipline} — {sd.channel}
            </option>
          ))}
        </select>
      </div>

      <p className="text-xs text-slate-500">
        Share of time each level of <strong>{subDisciplineKey}</strong> spends on each OMCL
        sub-phase. Columns should sum to <strong>100%</strong>.
      </p>

      <div className="rounded-lg border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2 sticky left-0 bg-slate-50">Phase / sub-phase</th>
              {levels.map((l) => (
                <th key={l.id} className="text-right px-3 py-2 tabular-nums">
                  {l.id}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {OMCL_PHASES.map((phase) => (
              <SubPhasesGroup
                key={phase.id}
                phaseLabel={phase.label}
                subPhases={subPhases.filter((sp) => sp.phaseId === phase.id)}
                levels={levels.map((l) => l.id)}
                find={find}
                set={set}
              />
            ))}
          </tbody>
          <tfoot className="bg-slate-50 sticky bottom-0">
            <tr>
              <td className="px-3 py-2 text-xs font-medium text-slate-500 sticky left-0 bg-slate-50">
                Column total
              </td>
              {levels.map((l) => {
                const sum = colSums[l.id] || 0;
                const staffed = staffedLevels.has(l.id);
                const ok = Math.abs(sum - 1) < 0.005;
                return (
                  <td
                    key={l.id}
                    className={clsx(
                      'px-3 py-2 text-right text-xs tabular-nums',
                      !staffed ? 'text-slate-400' : ok ? 'text-emerald-600' : 'text-rose-600',
                    )}
                    title={staffed ? undefined : 'This sub-discipline is not staffed at this level'}
                  >
                    {staffed ? `${(sum * 100).toFixed(1)}%` : '\u2014'}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function SubPhasesGroup({
  phaseLabel,
  subPhases,
  levels,
  find,
  set,
}: {
  phaseLabel: string;
  subPhases: readonly { id: string; label: string }[];
  levels: LevelId[];
  find: (subPhaseId: string, levelId: LevelId) => TimeConcentrationRow | undefined;
  set: (subPhaseId: string, levelId: LevelId, share: number) => void;
}) {
  return (
    <>
      <tr className="bg-slate-50/60">
        <td
          colSpan={levels.length + 1}
          className="px-3 py-1.5 text-xs uppercase tracking-wider text-slate-500 font-medium sticky left-0 bg-slate-50/60"
        >
          {phaseLabel}
        </td>
      </tr>
      {subPhases.map((sp) => (
        <tr key={sp.id} className="border-t border-slate-100">
          <td className="px-3 py-1.5 text-slate-700 text-xs sticky left-0 bg-white">
            <span className="pl-3">{sp.label}</span>
          </td>
          {levels.map((l) => {
            const cur = find(sp.id, l)?.share ?? 0;
            return (
              <td key={l} className="px-1 py-1 align-middle min-w-[78px]">
                <input
                  type="number"
                  step={0.01}
                  min={0}
                  max={1}
                  className="input py-1 px-2 w-full text-right text-xs tabular-nums"
                  value={cur}
                  onChange={(e) => set(sp.id, l, Number(e.target.value))}
                />
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}
